import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  ArrowBack,
  Receipt
} from '@mui/icons-material';
import PaymentService from './PaymentService';
import { styled, keyframes } from '@mui/material/styles';

// Animasyonlar
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const AnimatedBox = styled(Box)`
  animation: ${fadeIn} 0.6s ease-out;
`;

const SuccessIcon = styled(CheckCircleOutline)`
  color: #4caf50;
  font-size: 80px;
  animation: ${pulse} 1s ease-in-out;
`;

const ErrorIcon = styled(ErrorOutline)`
  color: #f44336;
  font-size: 80px;
  animation: ${pulse} 1s ease-in-out;
`;

/**
 * Ödeme callback sayfası
 * 3D Secure doğrulama sonrası buraya yönlendirilir
 */
const PaymentCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState(null);

  // URL'den gelen parametreleri al
  const isSuccess = location.pathname.includes('/payment/success');
  const authenticationResponse = searchParams.get('AuthenticationResponse');
  const statusParam = searchParams.get('status');
  const orderIdParam = searchParams.get('orderId');
  const responseCodeParam = searchParams.get('responseCode');
  const responseMessageParam = searchParams.get('responseMessage');
  
  useEffect(() => {
    processPaymentCallback();
  }, []);

  /**
   * 3D Secure callback'ini işle
   */
  const processPaymentCallback = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 PaymentCallback - Tüm URL parametreleri:', {
        pathname: location.pathname,
        search: location.search,
        isSuccess,
        statusParam,
        hasAuthResponse: !!authenticationResponse,
        orderIdParam,
        responseCodeParam,
        responseMessageParam
      });
      
      // Eğer backend zaten fail olarak işaretlemişse
      if (statusParam === 'fail' && !authenticationResponse) {
        console.error('❌ Backend fail döndü, AuthenticationResponse yok');
        throw new Error(responseMessageParam || 'Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
      }
      
      // AuthenticationResponse yoksa ama success ise - parametrelerle devam et
      if (!authenticationResponse) {
        console.warn('⚠️ AuthenticationResponse parametresi yok, diğer parametreler kontrol ediliyor...');
        
        if (responseCodeParam === '00' && orderIdParam) {
          console.log('✅ ResponseCode=00 ve OrderId var, başarılı kabul ediliyor');
          setPaymentResult({
            success: true,
            message: 'Ödeme başarıyla tamamlandı',
            merchantOrderId: orderIdParam,
            amount: null
          });
          setLoading(false);
          return;
        }
        
        console.error('AuthenticationResponse ve alternatif parametreler yok:', {
          search: location.search,
          state: location.state,
          allParams: Object.fromEntries(searchParams.entries())
        });
        throw new Error('Ödeme doğrulama bilgisi bulunamadı');
      }

      // URL decode et
      const decodedResponse = decodeURIComponent(authenticationResponse);
      console.log('🔍 3D Secure AuthenticationResponse (decoded):', decodedResponse);
      
      // XML'i basit parse et (production'da xml2js kullanılmalı)
      const parseXMLResponse = (xml) => {
        const result = {};
        
        // ResponseCode
        const responseCodeMatch = xml.match(/<ResponseCode>([^<]+)<\/ResponseCode>/);
        if (responseCodeMatch) result.responseCode = responseCodeMatch[1];
        
        // ResponseMessage
        const responseMessageMatch = xml.match(/<ResponseMessage>([^<]+)<\/ResponseMessage>/);
        if (responseMessageMatch) result.responseMessage = responseMessageMatch[1];
        
        // MD değeri
        const mdMatch = xml.match(/<MD>([^<]+)<\/MD>/);
        if (mdMatch) result.md = mdMatch[1];
        
        // MerchantOrderId
        const orderIdMatch = xml.match(/<MerchantOrderId>([^<]+)<\/MerchantOrderId>/);
        if (orderIdMatch) result.merchantOrderId = orderIdMatch[1];
        
        // Amount
        const amountMatch = xml.match(/<Amount>([^<]+)<\/Amount>/);
        if (amountMatch) result.amount = amountMatch[1];
        
        // ProvisionNumber
        const provisionMatch = xml.match(/<ProvisionNumber>([^<]+)<\/ProvisionNumber>/);
        if (provisionMatch) result.provisionNumber = provisionMatch[1];
        
        // RRN
        const rrnMatch = xml.match(/<RRN>([^<]+)<\/RRN>/);
        if (rrnMatch) result.rrn = rrnMatch[1];
        
        return result;
      };
      
      const parsedResponse = parseXMLResponse(decodedResponse);
      console.log('Parsed 3D Secure response:', parsedResponse);
      
      // Başarılı doğrulama kontrolü
      if (parsedResponse.responseCode !== '00') {
        console.error('3D Secure doğrulama hatası:', parsedResponse);
        throw new Error(parsedResponse.responseMessage ? `${parsedResponse.responseMessage} (Kod: ${parsedResponse.responseCode})` : 'Kart doğrulama başarısız');
      }

      const amountTl = parsedResponse.amount
        ? (Number(parsedResponse.amount) / 100).toFixed(2)
        : null;
      
      // Ödemeyi onayla
      const confirmResult = await PaymentService.confirmPayment({
        merchantOrderId: parsedResponse.merchantOrderId,
        amount: amountTl ? Number(amountTl) : Number(parsedResponse.amount) / 100,
        md: parsedResponse.md,
        responseCode: parsedResponse.responseCode,
        responseMessage: parsedResponse.responseMessage
      });
      
      if (confirmResult.success) {
        setPaymentResult({
          success: true,
          message: confirmResult.message,
          merchantOrderId: parsedResponse.merchantOrderId,
          provisionNumber: parsedResponse.provisionNumber,
          amount: amountTl
        });
      } else {
        throw new Error(confirmResult.message || 'Ödeme onaylanamadı');
      }
      
    } catch (err) {
      console.error('Payment callback error:', err);
      if (authenticationResponse) {
        try {
          console.error('3D Secure başarısız yanıt:', decodeURIComponent(authenticationResponse));
        } catch (decodeErr) {
          console.error('AuthenticationResponse decode edilemedi:', decodeErr);
        }
      }
      setError(err.message || 'Ödeme işlemi sırasında bir hata oluştu');
      setPaymentResult({
        success: false,
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ana sayfaya dön (Dashboard)
   */
  const handleGoHome = () => {
    navigate('/dashboard');
  };

  // Loading durumu
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Ödeme İşleniyor
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Lütfen bekleyin, ödemeniz onaylanıyor...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Başarılı ödeme
  if (paymentResult?.success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <AnimatedBox>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <SuccessIcon />
            
            <Typography variant="h4" gutterBottom sx={{ mt: 3, color: '#4caf50' }}>
              Ödeme Başarılı!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }}>
              Kontör satın alma işleminiz başarıyla tamamlandı.
            </Typography>
            
            <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Receipt sx={{ verticalAlign: 'middle', mr: 1 }} />
                  İşlem Detayları
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>İşlem No:</strong> {paymentResult.merchantOrderId}
                  </Typography>
                  {paymentResult.provisionNumber && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Onay Kodu:</strong> {paymentResult.provisionNumber}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tutar:</strong> {paymentResult.amount} TL
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tarih:</strong> {new Date().toLocaleString('tr-TR')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoHome}
                startIcon={<ArrowBack />}
                size="large"
              >
                Ana Sayfaya Dön
              </Button>
            </Box>
          </Paper>
        </AnimatedBox>
      </Container>
    );
  }

  // Başarısız ödeme
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <AnimatedBox>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon />
          
          <Typography variant="h4" gutterBottom sx={{ mt: 3, color: '#f44336' }}>
            Ödeme Başarısız
          </Typography>
          
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error || paymentResult?.message || 'Ödeme işlemi tamamlanamadı'}
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 4 }}>
            Ödeme işleminiz tamamlanamadı. Lütfen kart bilgilerinizi kontrol ederek tekrar deneyin.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoHome}
              startIcon={<ArrowBack />}
              size="large"
            >
              Ana Sayfaya Dön
            </Button>
          </Box>
        </Paper>
      </AnimatedBox>
    </Container>
  );
};

export default PaymentCallback;
